import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { DownloadContent } from '@/components/DownloadModal';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/Drawer';
import VisuallyHidden from '@/components/VisuallyHidden';
import useFormatDateTime from '@/hooks/useFormatDateTime';
import {
	useHasBeenSubscribed,
	useIsCollaborator,
	useSubscribed
} from '@/hooks/user';
import Download from '@/icons/download.svg';
import Edit from '@/icons/edit.svg';
import Folder from '@/icons/folder.svg';
import More from '@/icons/more.svg';
import routes from '@/routes';
import {
	setIsModalUpgradePlanOpen,
	setModalUpgradePlanScope
} from '@/store/profile';
import { formatFileSize } from '@/utils/index';

import Timer from './Timer';
import { useOptions, usePendingDownload } from './hooks';
import { DialogItem } from './modals';

export function MobileCell({ row }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isCollaborator = useIsCollaborator();
	const subscribed = useSubscribed();
	const hasBeenSubscribed = useHasBeenSubscribed();

	const { defaultDownloadOpen, clearPending } = usePendingDownload({
		id: row.original.id
	});

	const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
	const formatDateTime = useFormatDateTime();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const isModalUpgradePlanOpen = useSelector(
		state => state.profile.isModalUpgradePlanOpen
	);

	const closeDrawer = () => {
		setDrawerOpen(false);
	};

	const {
		isPaidOption,
		options,
		openOption,
		setOpenOption,
		shouldOpenOption
	} = useOptions();

	const {
		folderName,
		metadata: { originalFileName },
		createdAt
	} = row.original;

	const handleOpenChange = nextValue => {
		if (!subscribed) {
			dispatch(setModalUpgradePlanScope('download'));
			dispatch(setIsModalUpgradePlanOpen(true));

			return;
		}

		if (!nextValue) {
			clearPending();
		}

		setIsDownloadDialogOpen(nextValue);
	};

	useEffect(() => {
		if (subscribed && defaultDownloadOpen) {
			setIsDownloadDialogOpen(true);
		}
	}, [subscribed, defaultDownloadOpen]);

	useEffect(() => {
		if (openOption) {
			closeDrawer();
		}

		if (!shouldOpenOption(openOption)) {
			dispatch(setIsModalUpgradePlanOpen(true));
			dispatch(setModalUpgradePlanScope(openOption));
		}
	}, [openOption]);

	useEffect(() => {
		if (!isModalUpgradePlanOpen) {
			setOpenOption(false);
		}
	}, [isModalUpgradePlanOpen]);

	const renderEditButton = () => (
		<Button
			size="iconMd"
			variant="outline"
			onClick={() =>
				navigate(generatePath(routes.editor, { id: row.original.id }))
			}
		>
			<Edit className="text-black" />
		</Button>
	);

	return (
		<div className="flex gap-4">
			<div className="flex h-12 w-12 items-center justify-center rounded bg-red-background">
				<div className="text-sm font-semibold leading-normal text-red">
					{row.original.metadata.type.toUpperCase()}
				</div>
			</div>
			<div className="flex max-w-[calc(100dvw-170px)] flex-col">
				<span className="mb-1 truncate text-xs font-semibold leading-5">
					{originalFileName}
				</span>
				<span className="text-xs leading-5 text-grey-500">
					{formatFileSize(row.original.metadata.fileSize)}
				</span>
				<span className="text-xs leading-5 text-grey-500">
					{formatDateTime(row.original.createdAt)}
				</span>

				{Boolean(folderName) && (
					<div className="mt-3 flex w-fit items-center justify-start gap-1 rounded-md bg-grey-100 p-1 text-grey-500">
						<Folder aria-hidden className="size-4" />
						<span className="mt-[1px] truncate text-[0.625rem] leading-4">
							{folderName}
						</span>
					</div>
				)}

				<div className="mt-3 flex items-center gap-2">
					{!hasBeenSubscribed && <Timer createdAt={createdAt} />}

					{!isCollaborator && renderEditButton()}

					<Drawer
						open={isDownloadDialogOpen}
						onOpenChange={handleOpenChange}
					>
						<DrawerTrigger asChild>
							<Button size="iconMd" variant="outline">
								<Download />
							</Button>
						</DrawerTrigger>
						<DrawerContent className="p-0 md:w-[606px]">
							<DownloadContent
								onOpenChange={handleOpenChange}
								selectedTransactions={[row.original]}
							/>
						</DrawerContent>
					</Drawer>

					<Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
						<DrawerTrigger asChild>
							<Button variant="outline" size="iconMd">
								<span className="sr-only">Open menu</span>
								<More className="h-4 w-4" />
							</Button>
						</DrawerTrigger>
						<DrawerContent className="gap-1 px-5 pb-8 pt-6">
							<VisuallyHidden asChild>
								<DrawerHeader>
									<DrawerTitle>
										{t('myDocuments.modals.mobileDescription')}
									</DrawerTitle>
									<DrawerDescription>
										{t('myDocuments.modals.mobileDescription')}
									</DrawerDescription>
								</DrawerHeader>
							</VisuallyHidden>

							<DialogItem
								closeMenu={closeDrawer}
								options={options}
								setOpenOption={setOpenOption}
								isPaidOption={isPaidOption}
								openOption={openOption}
							/>
						</DrawerContent>
						{options.map(({ Component, name }) => (
							<Component
								key={name}
								open={shouldOpenOption(name)}
								onOpenChange={setOpenOption}
								selectedTransactions={[row.original]}
							/>
						))}
					</Drawer>
				</div>
			</div>
		</div>
	);
}
